import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Skeleton, SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  FILTROS_VACIOS, consulta, descargar, faltaElegir, useCatalogo, useDatos,
  type DatosDelInforme, type Filtros,
} from './api';
import { ControlesDeFiltro, mesDeHoy } from './filtros';
import { CabeceraDeInforme, informeDe } from './Informes';

/**
 * Informe — un informe del catálogo: sus filtros, su vista previa y su descarga.
 *
 * ## Lo que cambia
 *
 * - **La vista previa escapa lo que pinta.** El vanilla hacía
 *   `'<td>' + v + '</td>'` con cada celda: son nombres de personas, motivos de
 *   auditoría, respuestas de encuesta y campos de solicitud. Un `<img
 *   onerror=…>` guardado en cualquiera de esos campos se ejecutaba en la
 *   pantalla de quien genera el informe. `test_escapado.py` **no lo veía**:
 *   busca por el nombre de la expresión y ahí se llamaba `v`.
 * - **La vista previa se rehace al cambiar un filtro**, en vez de quedarse con
 *   los datos del filtro anterior hasta que alguien pulse «Vista previa».
 * - **Un informe que necesita elegir algo lo dice antes de pedirlo**: el de
 *   encuesta respondía 400 y el de solicitudes devolvía una fila de aviso.
 */
export function Informe({ clave }: { clave: string }) {
  const catalogo = useCatalogo();
  const informe = informeDe(catalogo.data?.reports, clave);
  const [filtros, setFiltros] = useState<Filtros>({ ...FILTROS_VACIOS, mes: mesDeHoy() });

  const falta = faltaElegir(informe, filtros);
  const datos = useDatos(clave, filtros, !!informe && !falta);

  if (catalogo.isPending) {
    return (
      <div aria-busy="true">
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (catalogo.isError) {
    return (
      <EstadoError
        err={catalogo.error}
        que="el catálogo de informes"
        onReintentar={() => void catalogo.refetch()}
        onVolver={() => { location.hash = '#informes'; }}
      />
    );
  }
  if (!informe) {
    return (
      <>
        <CabeceraDeInforme icono="bi-question-circle" titulo="Informe" />
        <EstadoVacio
          icono="bi-question-circle"
          titulo="Ese informe no existe"
          descripcion={`No hay ningún informe con la clave «${clave}».`}
          accion={{ texto: 'Ver los informes', onClick: () => { location.hash = '#informes'; } }}
        />
      </>
    );
  }

  return (
    <>
      <CabeceraDeInforme icono={informe.icon} titulo={informe.name} />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3 p-4">
          {catalogo.data && (
            <ControlesDeFiltro
              catalogo={catalogo.data}
              pide={informe.filters}
              filtros={filtros}
              onCambio={(cambio) => setFiltros((f) => ({ ...f, ...cambio }))}
            />
          )}
          <div className="flex-1" />
          <Button
            onClick={() => void descargar(`/reports/${clave}/export?${consulta(filtros)}`)}
            disabled={!!falta}
            razonDeshabilitado={falta ? `Elige ${falta} primero` : undefined}
          >
            <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Descargar Excel
          </Button>
        </div>
      </Card>

      {falta ? (
        <EstadoVacio
          compacto
          icono="bi-funnel"
          titulo={`Elige ${falta}`}
          descripcion="Este informe se arma sobre lo que elijas arriba."
        />
      ) : datos.isPending ? (
        <SkeletonFilas filas={6} columnas={5} />
      ) : datos.isError ? (
        <EstadoError
          err={datos.error}
          que="el informe"
          onReintentar={() => void datos.refetch()}
        />
      ) : !datos.data?.total ? (
        <EstadoVacio
          icono="bi-inbox"
          titulo="Sin datos"
          descripcion="No hay información para los filtros elegidos."
        />
      ) : (
        <Tabla datos={datos.data} atenuada={datos.isPlaceholderData} />
      )}
    </>
  );
}

function Tabla({ datos, atenuada }: { datos: DatosDelInforme; atenuada: boolean }) {
  return (
    <Card className={atenuada ? 'opacity-60' : undefined}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <h2 className="text-3 font-bold text-brand-ink">
          <i className="bi bi-table" aria-hidden="true" /> {datos.name}
        </h2>
        <p className="text-2 text-muted">
          {datos.total} {datos.total === 1 ? 'registro' : 'registros'}
          {datos.total > datos.rows.length && ` · se ven ${datos.rows.length}`}
        </p>
      </header>
      {/* El alto se acota para que la tabla no empuje la cabecera fuera de la
          pantalla, y la primera fila se queda pegada arriba al desplazar. */}
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full border-collapse text-2">
          <thead className="sticky top-0 bg-surface">
            <tr className="border-b border-line text-left uppercase tracking-wide text-muted">
              {datos.columns.map((c, i) => (
                <th key={i} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {datos.rows.map((fila, i) => (
              <tr key={i} className="border-b border-line last:border-0">
                {fila.map((valor, j) => (
                  <td key={j} className="px-3 py-1.5 align-top text-ink-2">
                    {valor === null || valor === undefined ? '' : String(valor)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
